import { apiClient } from './client';
import type {
  AiFeatureSummary,
  BedrockModelInfo,
  GrammarCheckResult,
  TranslateResult,
} from '@peles/shared';
import { extractApiErrorMessage } from '../utils/apiError';

/**
 * The AI flows this application has, and where each one's model is configured.
 *
 * Asked of the server rather than read from a constant here: the backend's
 * registry is what actually resolves a model and bills a call, and a second
 * list in the frontend that must agree with it fails silently — a picker
 * writing a setting nothing reads.
 */
export async function fetchAiFeatures(): Promise<AiFeatureSummary[]> {
  const { data } = await apiClient.get<AiFeatureSummary[]>('/ai/features');
  return data;
}

/** List Bedrock foundation models for the App Settings model picker. */
export async function fetchBedrockModels(): Promise<BedrockModelInfo[]> {
  const { data } = await apiClient.get<BedrockModelInfo[]>('/ai/models');
  return data;
}

/** Run a spelling/grammar pass over a draft message. */
export async function checkGrammar(text: string, tone?: string): Promise<GrammarCheckResult> {
  try {
    const { data } = await apiClient.post<GrammarCheckResult>('/ai/grammar-check', {
      text,
      ...(tone ? { tone } : {}),
    });
    return data;
  } catch (err) {
    // Surface the server's message (e.g. the Bedrock failure reason) rather
    // than the generic "Request failed with status code 502".
    throw new Error(extractApiErrorMessage(err, 'The grammar assistant is unavailable.'));
  }
}

/** Translate a draft message into another language. */
export async function translateDraft(
  text: string,
  targetLanguage: string,
  tone?: string,
): Promise<TranslateResult> {
  try {
    const { data } = await apiClient.post<TranslateResult>('/ai/translate', {
      text,
      targetLanguage,
      ...(tone ? { tone } : {}),
    });
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'The translation assistant is unavailable.'));
  }
}
